import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SettingsIcon, ShareIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

import type { AdditionalData } from '../../api/get-digital-twins';

type CreateDigiTwinShareType = AdditionalData & { expiresIn: string };

type ShareSettingKeys = Exclude<keyof AdditionalData, 'editingMode' | 'settings'>;

const DefaultFormValues: CreateDigiTwinShareType = {
  expiresIn: '60',
  editingMode: 'view',
  settings: true,
  showMeasurements: true,
  showRoomArea: true,
  roundRoomArea: true,
  showFurniture: true,
  showCompass: true,
  showRoofs: true,
  showFocusPoints: true,
  showExterior: true,
  transparentWalls3d: true,
  snappingActive: false,
};

const ShareSettings: Record<ShareSettingKeys, Array<CreateDigiTwinShareType['editingMode']>> = {
  showMeasurements: ['view', 'limited'],
  showRoomArea: ['view', 'limited'],
  roundRoomArea: ['view', 'limited'],
  showFurniture: ['view', 'limited'],
  showCompass: ['view', 'limited'],
  showRoofs: ['view', 'limited'],
  showFocusPoints: ['view', 'limited'],
  showExterior: ['view', 'limited'],
  transparentWalls3d: ['view', 'limited'],
  snappingActive: ['limited'],
};

export const ShareDigiTwinPopup: React.FC<
  React.PropsWithChildren & {
    onShare: (values: CreateDigiTwinShareType) => void;
    onCancel: () => void;
    onOpen: () => void;
    open?: boolean;
    defaultValues?: CreateDigiTwinShareType;
  }
> = ({ children, open, defaultValues, onOpen, onShare, onCancel }) => {
  const { t } = useTranslation();
  const btnTriggerRef = useRef<HTMLButtonElement>(null);

  const [settingsOpened, setSettingsOpened] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>();

  const methods = useForm<CreateDigiTwinShareType>({
    defaultValues: defaultValues || DefaultFormValues,
  });

  const { watch, reset } = methods;

  const editingMode = watch('editingMode');

  const handleChangeEditingMode = (editingMode: AdditionalData['editingMode']) => {
    const optionsNotIncluded = Object.keys(ShareSettings).filter(
      (op) => !ShareSettings[op as ShareSettingKeys].includes(editingMode)
    );
    reset((preValues) => ({
      ...preValues,
      ...optionsNotIncluded.reduce<Record<string, boolean>>((acc, item) => {
        acc[item] = false;
        return acc;
      }, {}),
      editingMode,
    }));
  };

  const handleChangeSettingAll = (checked: boolean) => {
    const options = Object.keys(ShareSettings).reduce<Record<string, boolean>>((acc, item) => {
      acc[item] = checked;
      return acc;
    }, {});
    reset((preValues) => ({
      ...preValues,
      ...options,
      settings: checked,
    }));
  };

  const handleChangeSettingOption = (option: ShareSettingKeys, checked: boolean) => {
    const updatedOptions = {
      ...methods.getValues(),
      [option]: checked,
    };

    const isCheckedAll = Object.entries(updatedOptions)
      .filter(([op]) => ShareSettings[op as ShareSettingKeys]?.includes(editingMode))
      .every(([, value]) => value === true);

    reset((preValues) => ({
      ...preValues,
      ...updatedOptions,
      settings: isCheckedAll,
    }));
  };

  const calculateContainerHeight = useCallback(() => {
    if (!btnTriggerRef.current) return;

    const rect = btnTriggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - rect.bottom - 50;
    setMaxHeight(availableHeight);
  }, []);

  useEffect(() => {
    if (open) {
      calculateContainerHeight();
      const isAnyToggleEnable = !defaultValues
        ? false
        : Object.keys(ShareSettings).some((key) => defaultValues[key as ShareSettingKeys] === true);
      setSettingsOpened(isAnyToggleEnable);
    } else {
      if (!defaultValues) {
        reset(DefaultFormValues);
      }
    }
  }, [reset, calculateContainerHeight, open, defaultValues]);

  useEffect(() => {
    window.addEventListener('resize', calculateContainerHeight, false);
    // Cleanup
    return () => {
      window.removeEventListener('resize', calculateContainerHeight, false);
    };
  }, [calculateContainerHeight]);

  return (
    <DropdownMenu open={open}>
      <DropdownMenuTrigger ref={btnTriggerRef} onClick={onOpen} asChild>
        {children}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" avoidCollisions={false} className="px-3 py-4">
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onShare)}
            className="overflow-y-auto"
            style={{ maxHeight }}
          >
            <div className="flex flex-col">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="font-semibold leading-tight">{t('share')}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 !p-1"
                  onClick={() => setSettingsOpened(!settingsOpened)}
                >
                  <SettingsIcon className="h-4 w-4 cursor-pointer" />
                </Button>
              </div>
              {settingsOpened ? (
                <div className="mb-3 space-y-2 rounded-md bg-background-lighter p-4">
                  <Controller
                    name="settings"
                    control={methods.control}
                    render={({ field }) => (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-text-secondary">{t('settings')}</p>
                        <Switch
                          checked={field.value}
                          aria-label={t('settings')}
                          onCheckedChange={handleChangeSettingAll}
                        />
                      </div>
                    )}
                  />
                  {Object.keys(ShareSettings).map((op) => {
                    const optionKey = op as ShareSettingKeys;
                    if (!ShareSettings[optionKey].includes(editingMode)) return null;
                    return (
                      <Controller
                        key={optionKey}
                        name={optionKey}
                        control={methods.control}
                        render={({ field }) => (
                          <div className="flex items-center justify-between">
                            <p className="text-sm">{t(op)}</p>
                            <Switch
                              aria-label={t(op)}
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                handleChangeSettingOption(optionKey, checked);
                              }}
                            />
                          </div>
                        )}
                      />
                    );
                  })}
                </div>
              ) : null}

              <Controller
                name="expiresIn"
                control={methods.control}
                render={({ field, formState }) => (
                  <Input
                    {...field}
                    type="number"
                    className="mb-3"
                    label={t('expires-in')}
                    errors={[formState.errors.expiresIn?.message || '']}
                  />
                )}
              />
              <Controller
                name="editingMode"
                control={methods.control}
                render={({ field }) => (
                  <>
                    <span className="mb-2 text-xs text-text-secondary">{t('editing-mode')}</span>
                    <div className="flex items-center gap-4">
                      <Button
                        className="w-full"
                        variant={field.value === 'view' ? 'contained' : 'outline'}
                        onClick={() => {
                          handleChangeEditingMode('view');
                        }}
                      >
                        {t('view-only')}
                      </Button>
                      <Button
                        className="w-full"
                        variant={field.value === 'limited' ? 'contained' : 'outline'}
                        onClick={() => {
                          handleChangeEditingMode('limited');
                        }}
                      >
                        {t('limited')}
                      </Button>
                    </div>
                  </>
                )}
              />
            </div>
            <hr className="my-6" />
            <div className="flex flex-row gap-4">
              <Button variant="contained" type="submit" className="w-full">
                <ShareIcon className="mr-2 !h-4 !w-4" />
                {t('share')}
              </Button>
              <Button variant="outline" className="w-full" onClick={onCancel}>
                {t('close')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
